import { clsx } from 'clsx'
import { type FC } from 'react'

import { IconCaretLeft, IconCaretRight } from '@redteclab/icons'

export const AlgoliaPaginationRenderType = {
  CURRENT_ACTIVE_PAGE: 'CURRENT_ACTIVE_PAGE',
  DOTS: 'DOTS',
  NEXT_PAGE: 'NEXT_PAGE',
  NUMBER: 'NUMBER',
  PREVIOUS_PAGE: 'PREVIOUS_PAGE',
} as const

export type AlgoliaPaginationRenderType =
  (typeof AlgoliaPaginationRenderType)[keyof typeof AlgoliaPaginationRenderType]

export type PaginationItem = {
  pageNumber: number
  renderType: AlgoliaPaginationRenderType
  url: string
}

type ProductSearchPaginationItemProps = {
  paginationItem: PaginationItem
  refine: () => void
}

const DELIMITER = '…'
const GO_TO_PAGE_LABEL = 'Go to page'
const QA_PREVIOUS_BUTTON = 'form-pagination-control-prev'
const QA_NEXT_BUTTON = 'form-pagination-control-next'

/*
  This gets the data-qa-id for previous and next pagination items which is needed for the QA automation process
*/
const getQaId = (
  renderType: AlgoliaPaginationRenderType,
): string | undefined => {
  switch (renderType) {
    case AlgoliaPaginationRenderType.NEXT_PAGE: {
      return QA_NEXT_BUTTON
    }
    case AlgoliaPaginationRenderType.PREVIOUS_PAGE: {
      return QA_PREVIOUS_BUTTON
    }
    default: {
      return undefined
    }
  }
}

export const ProductSearchPaginationItem: FC<
  ProductSearchPaginationItemProps
> = ({ paginationItem, refine }) => {
  const { pageNumber, renderType, url } = paginationItem

  // We need to add +1 for the Pagination Items that are built based on Algolia's hooks.
  const visualPageNumber = pageNumber + 1
  const isArrowButton =
    AlgoliaPaginationRenderType.NEXT_PAGE === renderType ||
    AlgoliaPaginationRenderType.PREVIOUS_PAGE === renderType
  const isCurrentActivePage =
    AlgoliaPaginationRenderType.CURRENT_ACTIVE_PAGE === renderType

  return (
    <a
      className={clsx(
        'flex-col rounded-lg p-0 no-underline hover:cursor-pointer',
        isCurrentActivePage && 'bg-light-primary-high hover:cursor-default',
        !isCurrentActivePage && 'bg-transparent',
        !isArrowButton && 'px-3.5 py-2',
      )}
      data-qa-id={getQaId(renderType)}
      href={url}
      onClick={(event) => {
        event.preventDefault()
        refine()
        window.scrollTo({ top: 0 })
      }}
    >
      <span
        className={clsx(
          'text-center text-s font-normal text-dark-primary-max hover:text-dark-brand',
          isCurrentActivePage &&
            'hover:cursor-default hover:text-dark-primary-max',
        )}
      >
        <span className="hidden">{GO_TO_PAGE_LABEL}</span>

        {renderType === AlgoliaPaginationRenderType.DOTS && (
          <span>{DELIMITER}</span>
        )}
        {(renderType === AlgoliaPaginationRenderType.NUMBER ||
          renderType === AlgoliaPaginationRenderType.CURRENT_ACTIVE_PAGE) && (
          <span>{visualPageNumber}</span>
        )}
        {renderType === AlgoliaPaginationRenderType.PREVIOUS_PAGE && (
          <IconCaretLeft className="size-6" />
        )}
        {renderType === AlgoliaPaginationRenderType.NEXT_PAGE && (
          <IconCaretRight className="size-6" />
        )}
      </span>
    </a>
  )
}
